import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import LeagueWizard from "../landing/wizard/LeagueWizard";

// "League settings" — the organiser changing a league that already exists.
//
// It IS the Create League wizard, opened on step 3: all four steps are on the
// stepper (Basics, Details, Settings, Review) with Settings selected, because the
// rail's "League settings" is about the rules, and the rest is a click away on the
// stepper. Same cards, same validation, same copy — nothing here is a second
// implementation that could drift from the flow that created the league.
//
// This screen's only jobs are to resolve the league from the slug and to prove
// the viewer runs it. Deliberately NOT the pre-revamp /contest/<slug>/edit form.
const LeagueSettings = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  const myId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null")?.id ?? null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setResolved(false);
    (async () => {
      try {
        const res = await getGameFromSlug({ slug });
        // A bad slug answers with HTTP 200 + success:false, so read the body.
        const data = res?.data?.success === false ? null : res?.data?.data;
        if (!cancelled) setGame(data?.id ? data : null);
      } catch (e) {
        console.error("League settings lookup failed:", e);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const isOrganizer = !!game && !!myId && String(game.user_id) === String(myId);

  useEffect(() => {
    if (!resolved) return;
    if (!game) {
      navigate("/contest", { replace: true });
      return;
    }
    // Settings belong to whoever runs the league. A member lands on its Home.
    if (!isOrganizer) navigate(`/contest/${game.vanity_url}`, { replace: true });
  }, [resolved, game, isOrganizer, navigate]);

  if (!resolved) {
    return (
      // Same offset the wizard renders at, so the skeleton doesn't jump on swap.
      <div style={{ paddingTop: 64 }} className="pb-12">
        <div className="max-w-screen-md mx-auto px-4 md:px-8 space-y-4">
          <div className="h-10 w-64 rounded-xl bg-[#F3F4F6] animate-pulse" />
          <div className="h-40 rounded-2xl bg-[#F3F4F6] animate-pulse" />
        </div>
      </div>
    );
  }
  if (!game || !isOrganizer) return null;

  return (
    // No offset wrapper: the wizard now renders its own fixed header and pads
    // itself by that header's height. Adding one here pushed everything down
    // twice, which is half of what "wrong header treatment" looked like.
    <>
      {/* The wizard reads its copy and its link prefix from the league's own type,
          so a private league edits with the private flow's wording. */}
      <LeagueWizard
        variant={game.game_type === "private" ? "private" : "public"}
        game={game}
      />
    </>
  );
};

export default LeagueSettings;
